import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClipboardCheck, Loader2, CheckCircle2, AlertTriangle, Clock, Ban, Users, LogOut, UserX, ExternalLink, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { JaritBadge } from "@/components/JaritBadge";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { EmployeeName } from "@/components/EmployeeName";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const todayMakassar = () => new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);

const STATE_STYLE = {
  Reviewed: "bg-[#E9F9EF] text-[#0E8A46]",
  "In Progress": "bg-[#E6F7F5] text-[#0E7C86]",
  "Needs Attention": "bg-[#FFF1E6] text-[#B5590F]",
  "Not Reviewed": "bg-[#F1F1F4] text-[#6E6E73]",
};

const SCard = ({ icon: Icon, label, value, color, title, onClick }) => (
  <div title={title} onClick={onClick} className={`liquid-glass-card flex items-center gap-3 p-4 ${onClick ? "cursor-pointer transition-all hover:shadow-md active:scale-[0.98]" : ""}`}>
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full" style={{ background: `${color}1A`, color }}>
      <Icon className="h-5 w-5" />
    </span>
    <div className="min-w-0">
      <p className="num text-[22px] font-bold leading-none">{value}</p>
      <p className="mt-1 text-[12px] text-[#8E8E93]">{label}</p>
    </div>
  </div>
);

export default function EndOfDayCheck() {
  const { readOnly } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [date, setDate] = useState(() => searchParams.get("date") || todayMakassar());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [peek, setPeek] = useState(null);

  const openPeek = async (title, keys) => {
    setPeek({ title, rows: null });
    try {
      const { data } = await api.get("/attendance/day-summary", { params: { date } });
      const g = data.groups || {};
      const rows = keys.flatMap((k) => g[k] || []).sort((a, b) => (a.nama || "").localeCompare(b.nama || ""));
      setPeek({ title, rows });
    } catch { setPeek({ title, rows: [] }); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/attendance/end-of-day", { params: { date } });
      setData(d);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const markReviewed = async (confirm) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/attendance/end-of-day/review", { date, confirm });
      if (!r.ok && r.warning) {
        setConfirmOpen(true);
        return;
      }
      toast.success("Day marked as reviewed");
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const s = data?.summary || {};
  const unresolved = data?.unresolved || [];

  return (
    <div data-testid="end-of-day-page" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-[22px] font-semibold tracking-tight sm:text-[28px]">
          <ClipboardCheck className="h-6 w-6 text-[#0071E3]" /> End-of-Day Attendance Check
        </h1>
        <p className="mt-0.5 text-[13px] text-[#8E8E93]">
          Verify the day's attendance is complete and reviewed. This does not change payroll.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          data-testid="eod-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-9 rounded-full border border-[#E1E1E6] bg-white/70 px-3 text-[13px]"
        />
        {data && (
          <span data-testid="eod-review-state" className={`rounded-full px-3 py-1.5 text-[12px] font-medium ${STATE_STYLE[data.review_state] || STATE_STYLE["Not Reviewed"]}`}>
            {data.review_state}{data.reviewed_by ? <>{" · by "}{data.reviewed_by} <VerifiedBadge size={12} /></> : ""}
          </span>
        )}
      </div>

      {loading && !data ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>
      ) : data?.before_golive ? (
        <div data-testid="eod-before-golive" className="liquid-glass-card grid place-items-center gap-2 px-6 py-14 text-center">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#FFF1E6] text-[#B5590F]"><Ban className="h-5 w-5" /></span>
          <p className="text-[14px] font-medium">Before Go-Live</p>
          <p className="max-w-md text-[13px] text-[#8E8E93]">Attendance processing has not started for this date.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <SCard icon={Users} label="Expected" value={s.expected ?? 0} color="#6E6E73" onClick={() => openPeek("Expected", ["full", "half", "zero", "working", "needs", "absent"])} />
            <SCard icon={CheckCircle2} label="Present" value={s.present ?? 0} color="#0E8A46" onClick={() => openPeek("Present", ["full", "half", "zero", "working", "needs"])} />
            <SCard icon={Fingerprint} label="Currently Working" value={s.currently_working ?? 0} color="#0E7C86" onClick={() => openPeek("Currently Working", ["working"])} />
            <SCard icon={CheckCircle2} label="Full Day" value={s.full_day ?? 0} color="#0E8A46" onClick={() => openPeek("Full Day", ["full"])} />
            <SCard icon={Clock} label="Half Day" value={s.half_day ?? 0} color="#0058C7" onClick={() => openPeek("Half Day", ["half"])} />
            <SCard icon={AlertTriangle} label="Needs Review" value={s.needs_review ?? 0} color="#B5590F" title="Attendance records for this date that still require review." onClick={() => openPeek("Needs Review", ["needs"])} />
            <SCard icon={LogOut} label="Missing Clock Out" value={s.missing_clock_out ?? 0} color="#B5590F" />
            <SCard icon={UserX} label="Not Arrived" value={s.not_arrived ?? 0} color="#8E8E93" onClick={() => openPeek("Not Arrived", ["absent"])} />
            <SCard icon={Ban} label="Ignored / Excluded" value={s.ignored ?? 0} color="#8E8E93" onClick={() => openPeek("Ignored / Excluded", ["ignored"])} />
          </div>

          <Dialog open={!!peek} onOpenChange={(v) => !v && setPeek(null)}>
            <DialogContent data-testid="eod-peek-dialog" className="max-h-[80vh] gap-0 overflow-hidden rounded-[20px] border border-white/50 bg-white/80 p-0 backdrop-blur-2xl sm:max-w-[460px]">
              <DialogHeader className="border-b border-black/[0.06] px-5 py-4 text-left">
                <DialogTitle className="text-[16px] font-semibold tracking-tight">{peek?.title} · {peek?.rows?.length ?? 0}</DialogTitle>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto p-3">
                {!peek?.rows ? (
                  <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>
                ) : peek.rows.length === 0 ? (
                  <p className="py-10 text-center text-[13.5px] text-[#8E8E93]">No one in this group.</p>
                ) : (
                  <div className="space-y-1.5">
                    {peek.rows.map((p) => (
                      <div key={p.employee_id || p.nip} data-testid="eod-peek-row" className="flex items-center gap-3 rounded-xl px-2.5 py-2 hover:bg-black/[0.03]">
                        <EmployeeAvatar id={p.employee_id} nama={p.nama} hasPhoto={p.has_photo} size={34} />
                        <div className="min-w-0 flex-1">
                          <p className="flex items-center gap-1.5 text-[13.5px] font-medium text-[#1D1D1F]"><EmployeeName id={p.employee_id} name={p.nama} />{p.verified && !p.is_jarit && <VerifiedBadge size={14} />}<JaritBadge show={p.is_jarit} /></p>
                          <p className="num text-[11.5px] text-[#8E8E93]">{p.nip || "—"}</p>
                        </div>
                        <span className="num shrink-0 text-[12px] text-[#6E6E73]">{p.clock_in || "--:--"} / {p.clock_out || "--:--"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {data?.day_in_progress && (
            <div data-testid="eod-in-progress-note" className="flex items-center gap-2 rounded-2xl border border-[#0E7C86]/20 bg-[#E6F7F5] px-4 py-3 text-[13px] text-[#0E7C86]">
              <Clock className="h-4 w-4 shrink-0" />
              The workday is still in progress — employees who have only clocked in are shown as “Currently Working”, not Missing Clock Out.
            </div>
          )}

          <div className="liquid-glass-card overflow-hidden">
            <div className="flex items-center gap-2 px-5 pb-3 pt-5">
              <AlertTriangle className="h-4 w-4 text-[#B5590F]" />
              <h2 className="text-[15px] font-semibold tracking-tight">Unresolved Attendance Issues</h2>
              <span className="num ml-1 rounded-full bg-[#FFF1E6] px-2 py-0.5 text-[11.5px] font-medium text-[#B5590F]">{unresolved.length}</span>
            </div>
            {unresolved.length === 0 ? (
              <p className="py-10 text-center text-[13.5px] text-[#8E8E93]">No unresolved issues for this date.</p>
            ) : (
              <div className="space-y-2.5 px-4 pb-4">
                {unresolved.map((u) => (
                  <div key={u.att_id} data-testid="eod-unresolved-row" className="flex items-center justify-between gap-2 rounded-2xl border border-black/[0.05] bg-white/70 p-3.5">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-[14px] font-semibold text-[#1D1D1F]"><EmployeeName id={u.employee_id} name={u.employee_name} />{u.verified && !u.is_jarit && <VerifiedBadge size={14} />}<JaritBadge show={u.is_jarit} /></p>
                      <p className="num text-[11.5px] text-[#8E8E93]">{u.employee_nip || "—"} · <span className="text-[#B5590F]">{u.exception_type}</span></p>
                    </div>
                    <button
                      data-testid="eod-review-attendance"
                      onClick={() => navigate(`/attendance/fingerprint?date=${date}&employee=${u.employee_id}`)}
                      className="flex shrink-0 items-center gap-1 rounded-full bg-[#0071E3] px-3 py-1.5 text-[12px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.97]"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Review
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!readOnly && (
            <button
              data-testid="eod-mark-reviewed"
              onClick={() => (unresolved.length ? setConfirmOpen(true) : markReviewed(true))}
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0071E3] py-3 text-[14px] font-semibold text-white transition-all hover:bg-[#0077ED] active:scale-[0.99] disabled:opacity-50 sm:w-auto sm:px-8"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />} Mark Day as Reviewed
            </button>
          )}
        </>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="eod-confirm-dialog" className="gap-0 rounded-[20px] border border-white/50 bg-white/80 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl sm:max-w-[440px]">
          <DialogHeader className="text-left">
            <DialogTitle className="text-[18px] font-semibold tracking-tight">Unresolved issues remain</DialogTitle>
            <p className="text-[13px] text-[#8E8E93]">
              {unresolved.length} attendance record(s) still require review. You can resolve them first, or confirm to mark the day reviewed anyway.
            </p>
          </DialogHeader>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setConfirmOpen(false)} className="rounded-full px-4 py-2 text-[13px] font-medium text-[#6E6E73] hover:bg-black/[0.04]">
              Review first
            </button>
            <button
              data-testid="eod-confirm-anyway"
              onClick={() => markReviewed(true)}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-full bg-[#0071E3] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#0077ED] disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Mark reviewed anyway
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
